// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import { ActionSheetContext, ActionSheetV2, Body, Button, LgPadding, XlSpacing } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { type RouteName } from '@alto/navigation';
import React, { useContext } from 'react';
import { CART_UPDATED_LOCATION, cartUpdatedCopyMapping } from '~shared/features/my-meds/constants';
import { getPlatformOS } from '~shared/helpers/getPlatformOS';

/**
 * The list of Checkout routes. This should match each route that is handled in the `checkoutGroup`.
 */
export const CHECKOUT_ROUTES: Partial<Record<RouteName, true>> = {
  RouteCart: true,
  RouteChangeQuantity: true,
  RouteCheckout: true,
  RouteCheckoutPhotoIdUpload: true,
  RouteNeedlePrompt: true,
  RouteOrderSuccess: true,
  RouteEditOrder: true,
};

/**
 * The list of Essentials routes. This should match each route that is handled in the `essentialsGroup`.
 */

export const ESSENTIALS_ROUTES: Partial<Record<RouteName, true>> = {
  RouteEssentialsFullScreen: true,
  RouteEssentialsDetails: true,
  RouteEssentialsCategoryDetails: true,
  RouteEssentialsSuggestFeedback: true,
};

/**
 * The list of Progyny Wizard routes. This should match each route that is handled in the `progynyWizardGroup`.
 */
export const PROGYNY_WIZARD_ROUTES: Partial<Record<RouteName, true>> = {
  RouteProgynyWizardIntro: true,
  RouteProgynyWizardIntro2: true,
  RouteProgynyWizardIntro3: true,
  RouteProgynyWizardStartDate: true,
  RouteProgynyWizardTriggerDate: true,
  RouteProgynyWizardDosageCheck: true,
  RouteProgynyWizardLeftoverMeds: true,
  RouteProgynyWizardCalculating: true,
  RouteProgynyWizardQuantityReview: true,
};

const useCartUpdatedLocation = () => {
  const { getCurrentRouteName } = useNavigation();
  const currentRouteName = getCurrentRouteName();

  if (currentRouteName) {
    // Porting over existing behavior from web
    if (getPlatformOS() === 'web') {
      if ((currentRouteName as string) === '/checkout') {
        return { cartUpdatedLocation: CART_UPDATED_LOCATION.CHECKOUT };
      } else {
        return { cartUpdatedLocation: CART_UPDATED_LOCATION.NON_SCHEDULING };
      }
    }

    // Safely check if currentRouteName is a valid route name
    const isCheckoutRoute = currentRouteName in CHECKOUT_ROUTES;
    const isEssentialsRoute = currentRouteName in ESSENTIALS_ROUTES;
    const isProgynyWizardRoute = currentRouteName in PROGYNY_WIZARD_ROUTES;

    if (isCheckoutRoute || isEssentialsRoute || isProgynyWizardRoute) {
      return { cartUpdatedLocation: CART_UPDATED_LOCATION.CHECKOUT };
    }
  }

  return { cartUpdatedLocation: CART_UPDATED_LOCATION.NON_SCHEDULING };
};

export const CartUpdatedActionSheet = () => {
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { navigate } = useNavigation();
  const { cartUpdatedLocation } = useCartUpdatedLocation();

  const handleConfirm = () => {
    closeActionSheet();

    if (cartUpdatedLocation === CART_UPDATED_LOCATION.CHECKOUT) {
      navigate('RouteAppTabNavigator', { screen: 'RouteTabMyMeds' });
    }
  };

  return (
    <ActionSheetV2
      title={cartUpdatedCopyMapping[cartUpdatedLocation].title}
      analyticsName="medication has changed"
      handleClose={handleConfirm}
    >
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <Body>{cartUpdatedCopyMapping[cartUpdatedLocation].body}</Body>
        <XlSpacing />
        <Button
          onPress={handleConfirm}
          label={cartUpdatedCopyMapping[cartUpdatedLocation].buttonText}
        />
      </LgPadding>
    </ActionSheetV2>
  );
};
